/**
 * Plan「数据分析 › 指标统计」数据层（后台页面对齐批 4 第 5 项；只读）。后端 `api/stats.py`，口径见 `eval/timing.py`。
 * 单独成文件（不往 plan-lab.ts 里加），和并行改别的页的同事错开。
 */
import { useQuery } from "@tanstack/react-query";

import { apiClient } from "@/lib/api-client";

const P = "/admin/v1/plan-lab/stats";

/** 一批的整批耗时（秒） */
export type PlanStatsBatch = {
	batch_id: string;
	kind: string;
	status: string | null;
	stop_reason: string | null;
	created_at: string | null;
	started_at: string | null;
	ended_at: string | null;
	bank_id: string | null;
	test_set: string | null;
	/** 测试集已退役（判分规则 EV-26）：历史批次照常列，集名旁标「已退役」 */
	test_set_retired?: boolean;
	scenario_count: number;
	total_trials: number;
	done: number;
	ran: number;
	undetermined: number;
	/** 未判定按原因分（限流 rate_limited 单列，10-21 A3） */
	undetermined_reasons?: Record<string, number>;
	concurrency: number;
	cost_usd: number | null;
	note: string | null;
	reruns: number;
	queue_wait_s: number | null;
	wall_s: number | null;
	trial_sum_s: number;
	parallelism: number | null;
	trial_p50_s: number | null;
	trial_p90_s: number | null;
	trial_max_s: number | null;
	trial_mean_s: number | null;
};

export type PlanStatsFamily = {
	family: string;
	trials: number;
	p50_s: number | null;
	p90_s: number | null;
	max_s: number;
	mean_s: number;
	sum_s: number;
	share: number;
};

export type PlanStatsSlowTrial = {
	trial_id: string;
	scenario_id: string;
	family: string | null;
	index: number | null;
	attempt: number | null;
	duration_s: number;
	outcome: string | null;
	reason: string | null;
	num_turns: number | null;
	termination_reason: string | null;
	cost_usd: number | null;
};

export type PlanStatsBatchDetail = PlanStatsBatch & {
	by_family: PlanStatsFamily[];
	slowest: PlanStatsSlowTrial[];
};

export type PlanStatsSegmentRow = {
	key: string;
	label: string;
	/** agent ／ agent.sub ／ sim ／ judge ／ cx ／ unrecorded ／ outside */
	group: string;
	seconds: number | null;
	share: number | null;
	calls: number | null;
	call_p50_ms: number | null;
	call_p90_ms: number | null;
	cost_usd: number | null;
};

export type PlanStatsSegments = {
	scope: "batch" | "days";
	days: number | null;
	batch_ids: string[];
	rows: PlanStatsSegmentRow[];
	outside: PlanStatsSegmentRow[];
	totals: {
		batches?: number;
		trials?: number;
		turns?: number;
		trial_sum_s?: number;
		wall_s?: number;
		parallelism?: number | null;
		recorded_s?: number;
		unrecorded_s?: number;
		llm_calls?: number;
		cost_usd?: number;
		tools_scanned?: boolean;
	};
};

/** 一组耗时（毫秒）的分布 */
export type PlanStatsDist = {
	n: number;
	p50: number | null;
	p90: number | null;
	max: number | null;
	mean: number | null;
	sum: number;
};

export type PlanStatsTurns = {
	days: number;
	source: string;
	since: string;
	by_source: (PlanStatsDist & { source: string; label: string })[];
	nodes: (PlanStatsDist & { key: string; label: string; calls_per_turn: number | null })[];
};

export function usePlanStatsBatches(limit: number) {
	return useQuery<{ rows: PlanStatsBatch[] }>({
		queryKey: ["plan-lab", "stats", "batches", limit],
		queryFn: async () => (await apiClient.get(`${P}/batches`, { params: { limit } })).data,
	});
}

export function usePlanStatsBatch(batchId: string | null) {
	return useQuery<PlanStatsBatchDetail>({
		queryKey: ["plan-lab", "stats", "batch", batchId],
		enabled: !!batchId,
		queryFn: async () =>
			(await apiClient.get(`${P}/batches/${encodeURIComponent(batchId ?? "")}`)).data,
	});
}

/** batchId 有值看单批；没有看近 days 天建的全部批次 */
export function usePlanStatsSegments(batchId: string | null, days: number, enabled = true) {
	return useQuery<PlanStatsSegments>({
		queryKey: ["plan-lab", "stats", "segments", batchId ?? `days-${days}`],
		enabled,
		queryFn: async () =>
			(await apiClient.get(`${P}/segments`, { params: batchId ? { batch_id: batchId } : { days } }))
				.data,
	});
}

export function usePlanStatsTurns(days: number, source: string | null) {
	return useQuery<PlanStatsTurns>({
		queryKey: ["plan-lab", "stats", "turns", days, source ?? "all"],
		queryFn: async () =>
			(await apiClient.get(`${P}/turns`, { params: source ? { days, source } : { days } })).data,
	});
}
